import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
  unsafeCSS,
  enableBodyScroll,
  disableBodyScroll,
} from '@hh/global.js'

class HHDetailsModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    heightOffset: {
      type: Number,
      attribute: 'height-offset',
    },
    _height: {
      state: true,
    },
    lockScroll: {
      type: Boolean,
      attribute: 'lock-scroll',
    },
    zIndex: {
      type: Number,
      attribute: 'z-index'
    },
    bodyClasses: {
      type: String,
      attribute: 'body-class'
    },
    ctx: {
      type: String,
    }
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    this.heightOffset = 3
    this.zIndex = '100'
    this.breakpoint = '(min-width: 750px)'
    this.bodyClasses = ''
    this.bodyListener = this.bodyListener.bind(this)
    this.lockScroll = false
    this.openTimeout = null
    this.ctx = ''
    this.isMouseHover = false
  }

  connectedCallback() {
    super.connectedCallback()
    document.body.addEventListener('closeModal', this.close.bind(this))
  }

  requestUpdate(name, value) {
    // Allows parents to close open modals
    if (name === 'forcedOpen') {
      this.toggle(false, !this.forcedOpen)
    }
    return super.requestUpdate(name, value)
  }

  updated(changedProperties) {
    clearTimeout(this.openTimeout)

    if (changedProperties.has('open')) {
      this.openTimeout = setTimeout(() => {
        if (this.open) {
          document.body.classList.add('overflow-hidden')
          if (this.ctx === 'megamenu') {
            document.body.classList.add(this.bodyClasses)
          }
        } else if (!this.open) {
          document.body.classList.remove('overflow-hidden')
          if (this.ctx === 'megamenu') {
            document.body.classList.remove(this.bodyClasses)
          }
        }
      }, 10)
    }
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  toggle(e = false, value = undefined) {
    if (
      e.type === 'keyup' &&
      e.code.toUpperCase() !== 'ARROWDOWN'
    ) {
      return true
    }

    e && e.preventDefault()

    this.open = typeof value === 'undefined' ? !this.open : value
    this[`${this.open ? 'add' : 'remove'}BodyListener`]()
    this.open && this.lockHeight && this.setHeight()
    this.open && this.broadcast()

    if (this.isMobile()) {
      const openSidePanel = document.querySelector(
        '[data-sidepanel]:not([hidden="true"])'
      )
      if (openSidePanel) openSidePanel.setAttribute('hidden', 'true')
    }
  }

  openMegamenu(e) {
    this.toggle(e, true)
    if (this.bodyClasses !== '') {
      document.body.classList.add(this.bodyClasses)
    }
    this.isMouseHover = true
  }

  toggleLeaveMegaMenu() {
    let leaveTimeout

    const handleMouseLeave = () => {
      this.isMouseHover = false

      leaveTimeout = setTimeout(() => {
        if (!this.isMouseHover && this.open) {
          this.toggle()
          this.removeEventListener('mouseleave', handleMouseLeave)
          this.removeEventListener('mouseenter', handleMouseEnter)
        }
      }, 1000)
    }

    const handleMouseEnter = () => {
      clearTimeout(leaveTimeout)
      this.isMouseHover = true
    }

    this.addEventListener('mouseleave', handleMouseLeave, false)
    this.addEventListener('mouseenter', handleMouseEnter, false)
  }


  closeFromDetails(e) {
    const path = e.composedPath()
    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.toggle()
    }
  }

  closeWithEscape(e) {
    if (e.code?.toUpperCase() === 'ESCAPE') {
      this.toggle()
    }
    // Added to support closing on Enter within Details panel
    if (
      e.code === 'Enter' &&
      e
        .composedPath()
        .some((element) => element.matches && element.matches('[data-close]'))
    ) {
      this.toggle()
    }
  }

  close() {
    this.open && this.toggle()
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  addBodyListener() {
    if (this.bodyClasses !== '') {
      document.body.classList.add(this.bodyClasses)
    }

    document.body.addEventListener('click', this.bodyListener)
  }

  removeBodyListener() {
    if (this.bodyClasses !== '') {
      document.body.classList.remove(this.bodyClasses)
    }

    document.body.removeEventListener('click', this.bodyListener)
  }

  bodyListener(e) {
    !e._intercepted && this.toggle()
  }

  setHeight() {
    const { top: parentTop = 0 } = this.parentNode.getBoundingClientRect() || {}
    const { top: detailsTop = 0 } =
      this.$details?.value.getBoundingClientRect() || {}

    this._height = this.isMobile()
      ? `calc(100vh - ${Math.floor(
        parentTop +
        detailsTop -
        this.getBoundingClientRect().x -
        this.heightOffset
      )}px)`
      : ''
  }

  toggleWithOutside(e) {
    const path = e.composedPath()
    if (path[0].classList.contains('content')) {
      this.toggle()
    }
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  get focusActive() {
    return !this.isMouseHover && this.open
  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <style>
        :host {
          z-index: var(--hh-z-index, ${unsafeCSS(this.zIndex)});
        }
      </style>
      <details
        ?open="${this.open}"
        @click="${this.interceptEvent}"
        ${ref(this.$container)}
      >
        <summary
          @keyup="${this.toggle}"
          @click="${this.toggle}"
          aria-expanded="${this.open}"
          aria-haspopup="dialog"
          role="button"
          ${ref(this.$container)}
        >
          <slot
            class="${classMap({ 'is-open': this.open })}"
            name="summary"
            tabindex="-1"
          ></slot>
          <slot
            @mouseleave="${this.toggleLeaveMegaMenu}"
            class="${classMap({ 'is-open': this.open })}"
            name="summaryOnHover"
            tabindex="-1"
          ></slot>
        </summary>
        <div
          class="content"
          role="dialog"
          aria-modal="true"
          aria-label="${this.label}"
          style="${styleMap({ height: this._height })}"
          ${ref(this.$details)}
          @click="${this.toggleWithOutside}"
        >
          <hh-focus-trap active="${this.focusActive}">
            <slot
              name="details"
              @keyup="${this.closeWithEscape}"
              @click="${this.closeFromDetails}"
            ></slot>
          </hh-focus-trap>
        </div>
      </details>`
  }

  static styles = css`
    summary {
      list-style-type: none;
    }

    summary:focus {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    :host([ctx='filters']) summary {
      width: 14rem;
      position: fixed;
      bottom: 3rem;
      height: 6rem;
      z-index: 10000;
      left: 50%;
      transform: translateX(-50%);
    }

    summary::-webkit-details-marker {
      display: none;
    }

    .content {
      position: var(--hh-position, absolute);
      top: var(--hh-top);
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--hh-modal-background);
      backdrop-filter: var(--hh-modal-backdrop-filter);
      -webkit-backdrop-filter: var(--hh-modal-backdrop-filter);
      height: var(--hh-modal-height);
      z-index: var(--hh-z-index-inner, 1);
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
    }
  `
}

customElements.define('hh-details-modal', HHDetailsModal)
